import React, { useMemo } from 'react';
import { DollarSign, Calendar, Star, Package } from 'lucide-react';
import { StatsGrid } from '../../admin';

const toNumber = (v, fallback = 0) => {
  if (v == null || v === '') return fallback;
  const n = typeof v === 'number' ? v : parseFloat(String(v), 10);
  return Number.isFinite(n) ? n : fallback;
};

const AnalyticsTab = ({ vendor, vendorProfile }) => {
  /**
   * Admin vendor API (`AdminVendorController@show`) loads profile with:
   * - `withSum(..., 'vendor_payout_amount')` → attribute `total_revenue` (completed bookings)
   * - `withCount('bookings')` → `bookings_count`
   * Legacy keys `total_earnings` / `total_bookings` are not set on that payload.
   */
  const totalEarnings = useMemo(() => {
    const raw =
      vendorProfile?.total_revenue ??
      vendorProfile?.totalRevenue ??
      vendorProfile?.total_earnings ??
      vendorProfile?.totalEarnings ??
      vendor?.total_earnings ??
      vendor?.totalEarnings ??
      0;
    return toNumber(raw, 0);
  }, [vendor, vendorProfile]);

  const totalBookings = useMemo(() => {
    const raw =
      vendorProfile?.bookings_count ??
      vendorProfile?.bookingsCount ??
      vendorProfile?.total_bookings ??
      vendorProfile?.totalBookings ??
      vendor?.total_bookings ??
      vendor?.totalBookings;
    if (raw != null && raw !== '') {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (Number.isFinite(n)) return n;
    }
    return vendor?.bookings?.length ?? 0;
  }, [vendor, vendorProfile]);

  const averageRating = vendorProfile?.average_rating ?? vendorProfile?.averageRating ?? 0;
  const totalReviews = vendorProfile?.total_reviews ?? vendorProfile?.totalReviews ?? vendor?.reviews?.length ?? 0;
  const totalServices = vendorProfile?.vendor_services?.length || vendorProfile?.vendorServices?.length || 0;
  const totalPackages =
    vendorProfile?.vendor_service_packages?.length || vendorProfile?.vendorServicePackages?.length || 0;

  const formatCurrency = (amount) => {
    const n = toNumber(amount, NaN);
    if (Number.isFinite(n)) {
      return `$${n.toFixed(2)}`;
    }
    return '$0.00';
  };

  const formatRating = (rating) => {
    if (rating && rating > 0) {
      return `${Number(rating).toFixed(1)}/5.0`;
    }
    return 'N/A';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Vendor Analytics</h3>
        <p className="text-sm text-gray-600 mt-1">Overview of vendor performance and statistics</p>
      </div>

      <StatsGrid 
        stats={[
          {
            key: 'earnings',
            title: 'Total Earnings',
            value: formatCurrency(totalEarnings),
            icon: DollarSign,
            variant: 'success'
          },
          {
            key: 'bookings',
            title: 'Total Bookings',
            value: totalBookings.toString(),
            icon: Calendar,
            variant: 'info'
          },
          {
            key: 'rating',
            title: 'Average Rating',
            value: formatRating(averageRating),
            icon: Star,
            variant: 'warning'
          },
          {
            key: 'reviews',
            title: 'Total Reviews',
            value: totalReviews.toString(),
            icon: Star,
            variant: 'default'
          },
          {
            key: 'services',
            title: 'Services',
            value: totalServices.toString(),
            icon: Package,
            variant: 'info'
          },
          {
            key: 'packages',
            title: 'Packages',
            value: totalPackages.toString(),
            icon: Package,
            variant: 'default'
          }
        ]}
        columns={3}
      />

      {/* Additional Analytics Section */}
      {/* <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Completion Rate</h4>
            <TrendingUp className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {totalBookings > 0 ? '100%' : 'N/A'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Based on completed bookings</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Response Time</h4>
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">N/A</p>
          <p className="text-xs text-gray-500 mt-1">Average response time</p>
        </div>
      </div> */}
    </div>
  );
};

export default AnalyticsTab;

